import {type CSSProperties, useState, useRef, useEffect} from 'react';
import {Overlay, Popover, Badge} from '@goproblems/ui';
import {SimilarProblem} from '@types';
import {ProblemCard} from './ProblemCard';
import {
    useFetchSimilarCountsQuery,
    useFetchSimilarProblemsQuery,
    useFetchSimilarProblemsPercentageQuery,
} from '@api';
import {useNavigate, Link} from 'react-router';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faCircleInfo} from '@fortawesome/free-solid-svg-icons';
import {t} from 'i18next';
import {isMobile} from 'react-device-detect';

const PotentialDuplicatesContent = ({
    title,
    problems = [],
    loading = false,
    closePopover,
    showPercentage = false,
}: {
    title: string;
    problems?: SimilarProblem[];
    loading?: boolean;
    closePopover?: () => void;
    showPercentage?: boolean;
}) => {
    const navigate = useNavigate();
    const overlayBadgeStyle = {
        backgroundColor:
            'color-mix(in srgb, var(--ui-surface-active) 68%, transparent)',
    } as CSSProperties;

    return (
        <>
            <div className={`${isMobile ? 'mb-1' : ''} font-bold text-lg`}>
                {title}
            </div>
            <div
                className={
                    isMobile
                        ? 'grid grid-cols-3 justify-items-center gap-x-1 gap-y-1.5'
                        : 'grid grid-cols-5 gap-2'
                }>
                {problems.length === 0 && !loading && (
                    <div className="col-span-full">
                        {t('common.nothing_here_yet')}
                    </div>
                )}
                {loading ? (
                    <div className="col-span-full">{t('common.loading')}</div>
                ) : (
                    problems.map(problem => (
                        <div
                            key={problem.id}
                            className={
                                isMobile
                                    ? 'relative w-[96px] sm:w-[104px]'
                                    : 'relative'
                            }>
                            <ProblemCard
                                problem={problem}
                                showBody={false}
                                showSolved={false}
                                rankBadgeStyle={overlayBadgeStyle}
                                className={
                                    isMobile
                                        ? 'w-full cursor-pointer text-base'
                                        : 'w-[150px] text-lg cursor-pointer'
                                }
                                onClick={() => {
                                    navigate(`/problems/${problem.id}`);
                                    if (closePopover) closePopover();
                                }}
                            />
                            {showPercentage &&
                                problem.matchPercentage !== null &&
                                problem.matchPercentage !== undefined && (
                                    <Badge
                                        bg="light"
                                        style={overlayBadgeStyle}
                                        className="absolute text-dark mb-1 me-1 rounded-sm right-0 bottom-0 text-[10px] leading-tight">
                                        {problem.matchPercentage}%
                                    </Badge>
                                )}
                        </div>
                    ))
                )}
            </div>
        </>
    );
};

export const PotentialDuplicates = ({
    pattern,
    excludedIds = [],
    r1Title = t('single_problem.approximate_match'),
    r2Title = t('single_problem.close_match'),
    customClassName = '',
    buttonClassName = '',
    showInfo = false,
    placement = 'left',
    buttonText = t('single_problem.similar_problems'),
    highlightWhenDuplicate,
    showPercentageInButton = true,
    autoLoadPercentage = false,
    showR1Count = true,
    compactButtonLabel = false,
}: {
    pattern: string;
    excludedIds?: number[];
    r1Title?: string;
    r2Title?: string;
    customClassName?: string;
    buttonClassName?: string;
    showInfo?: boolean;
    placement?: 'left' | 'right' | 'top' | 'bottom';
    buttonText?: string;
    highlightWhenDuplicate?: boolean;
    showPercentageInButton?: boolean;
    autoLoadPercentage?: boolean;
    showR1Count?: boolean;
    compactButtonLabel?: boolean;
}) => {
    const [isVisible, setIsVisible] = useState(false);
    const [loadPercentage, setLoadPercentage] = useState(autoLoadPercentage);
    const target = useRef(null);
    const hasSolution = pattern.includes('RIGHT');

    const {data: counts, isLoading: countsLoading} = useFetchSimilarCountsQuery(
        {
            pattern,
            excludedIds,
        },
        {skip: !hasSolution}
    );

    const {data: r2Problems, isLoading: r2Loading} =
        useFetchSimilarProblemsQuery(
            {
                pattern,
                radii: [2],
                excludedIds,
                limit: 20,
            },
            {skip: !hasSolution || !isVisible}
        );

    const r2ProblemIds = hasSolution
        ? (r2Problems?.entries?.map(p => p.id) ?? [])
        : [];

    const {
        data: percentageData,
        isLoading: percentageLoading,
        isFetching: percentageFetching,
    } = useFetchSimilarProblemsPercentageQuery(
        {
            pattern,
            excludedIds,
            includedIds: r2ProblemIds,
            limit: 20,
        },
        {skip: !hasSolution || !loadPercentage || r2Loading}
    );

    useEffect(() => {
        setIsVisible(false);
        setLoadPercentage(autoLoadPercentage);
    }, [pattern, autoLoadPercentage]);

    // When popup opens, trigger percentage loading
    useEffect(() => {
        if (hasSolution && isVisible && !loadPercentage) {
            setLoadPercentage(true);
        }
    }, [hasSolution, isVisible, loadPercentage]);

    const topPercentageText =
        hasSolution && showPercentageInButton && percentageFetching
            ? '...'
            : hasSolution &&
                showPercentageInButton &&
                percentageData &&
                !percentageLoading
              ? `${percentageData.topPercentage}%`
              : '';

    // Enrich r2 problems with percentage from percentage data
    const r2ProblemsWithPercentage = (() => {
        if (
            !r2Problems?.entries ||
            !percentageData?.entries ||
            percentageFetching
        ) {
            return r2Problems?.entries;
        }
        const percentageMap = new Map(
            percentageData.entries.map(p => [p.id, p.matchPercentage])
        );
        return r2Problems.entries.map(p => ({
            ...p,
            matchPercentage: percentageMap.get(p.id),
        }));
    })();

    useEffect(() => {
        if (!isMobile) return;
        if (!isVisible) return;

        const frameId = window.requestAnimationFrame(() => {
            window.dispatchEvent(new Event('resize'));
        });

        return () => {
            window.cancelAnimationFrame(frameId);
        };
    }, [
        isVisible,
        r2Loading,
        percentageLoading,
        percentageFetching,
        r2ProblemsWithPercentage?.length,
        percentageData?.entries?.length,
    ]);

    return (
        <div className="potential-duplicates-container">
            <div className={`potential-duplicates-button ${customClassName}`}>
                <div
                    ref={target}
                    tabIndex={0}
                    onClick={() => {
                        if (hasSolution) setIsVisible(!isVisible);
                    }}
                    className={`btn link p-0 ${highlightWhenDuplicate && counts?.r2Count && counts.r2Count > 0 ? 'text-red-500' : ''} ${buttonClassName}`}>
                    {showInfo && (
                        <Link
                            to="/article/solutionsignatures"
                            target="_blank"
                            className="mr-0.5">
                            <FontAwesomeIcon
                                icon={faCircleInfo}
                                className="text-muted"
                                size="sm"
                            />
                        </Link>
                    )}
                    <span className={compactButtonLabel ? 'sr-only' : ''}>
                        {buttonText}
                    </span>
                    {!hasSolution ? (
                        <span>
                            ({t('single_problem.waiting_for_solution_path')})
                        </span>
                    ) : (
                        counts &&
                        !countsLoading && (
                            <span>
                                (
                                {showPercentageInButton
                                    ? `${counts?.r2Count}${topPercentageText ? `, ${topPercentageText}` : ''}`
                                    : showR1Count
                                      ? `${counts?.r2Count}, ${counts?.r1Count}`
                                      : `${counts?.r2Count}`}
                                )
                            </span>
                        )
                    )}
                </div>

                <Overlay
                    target={target.current}
                    show={hasSolution && isVisible}
                    placement={placement}
                    containerPadding={isMobile ? 8 : undefined}
                    popperConfig={isMobile ? {strategy: 'fixed'} : undefined}
                    rootClose
                    onHide={() => setIsVisible(false)}>
                    <Popover
                        id="popover-contained"
                        style={
                            {
                                width: isMobile ? 'fit-content' : 'auto',
                                maxWidth: isMobile
                                    ? 'min(92vw, 22rem)'
                                    : 'none',
                            } as React.CSSProperties
                        }>
                        <Popover.Header as="h3">
                            {buttonText}
                            <Link
                                to="/article/solutionsignatures"
                                target="_blank"
                                className="ms-2">
                                <FontAwesomeIcon
                                    icon={faCircleInfo}
                                    className="text-muted"
                                    size="sm"
                                />
                            </Link>
                        </Popover.Header>
                        <Popover.Body>
                            <div
                                id="potential-duplicates-content"
                                className={
                                    isMobile ? 'w-fit max-w-full' : undefined
                                }>
                                <PotentialDuplicatesContent
                                    title={r2Title}
                                    problems={r2ProblemsWithPercentage}
                                    loading={r2Loading}
                                    closePopover={() => setIsVisible(false)}
                                    showPercentage
                                />
                                <div className={isMobile ? 'mt-1.5' : 'mt-2'}>
                                    <PotentialDuplicatesContent
                                        title={r1Title}
                                        problems={percentageData?.entries}
                                        loading={
                                            percentageLoading ||
                                            percentageFetching
                                        }
                                        closePopover={() => setIsVisible(false)}
                                        showPercentage
                                    />
                                </div>
                            </div>
                        </Popover.Body>
                    </Popover>
                </Overlay>
            </div>
        </div>
    );
};
